import moment from 'moment';
import * as React from 'react';
import { useCallback } from 'react';
import { FallbackEpisode } from '../../../types/fallbackTrackingTypes';
import SemanticDataTable, { SemanticDataTableColumn } from '../../shared/semanticTable/SemanticDataTable';
import { formatSeconds, formatSignedSeconds } from './fallbackTrackingReport';

interface Props {
    episodes: FallbackEpisode[];
    selectedEpisode?: FallbackEpisode;
    onSelect: (episode: FallbackEpisode) => void;
}

/** Episodes per page: keeps the table light for days with thousands of episodes. */
const PAGE_SIZE = 25;

const formatTime = (time: string) => moment.parseZone(time).format('h:mm:ss A');

const COLUMNS: SemanticDataTableColumn<FallbackEpisode>[] = [
    { id: 'route', title: 'Route', render: e => e.routeShortName, getSortValue: e => e.routeShortName },
    { id: 'vehicle', title: 'Vehicle', render: e => e.vehicleId, getSortValue: e => e.vehicleId },
    { id: 'trip', title: 'Trip', render: e => e.tripId, getSortValue: e => e.tripId },
    { id: 'start', title: 'Last position', render: e => formatTime(e.start), getSortValue: e => moment.parseZone(e.start).valueOf() },
    { id: 'end', title: 'Next position', render: e => formatTime(e.end), getSortValue: e => moment.parseZone(e.end).valueOf() },
    { id: 'gap', title: 'Gap', render: e => formatSeconds(e.durationSec), getSortValue: e => e.durationSec },
    { id: 'delay', title: 'Kept delay', render: e => formatSeconds(e.observedDelaySec), getSortValue: e => e.observedDelaySec },
    { id: 'recoveryDelay', title: 'Delay at recovery', render: e => formatSeconds(e.recoveryDelaySec), getSortValue: e => e.recoveryDelaySec },
    { id: 'delayError', title: 'Delay error', render: e => formatSignedSeconds(e.delayErrorSec), getSortValue: e => e.delayErrorSec },
    {
        id: 'outcome',
        title: 'Outcome',
        render: e => <span className={e.outcome === 'Expired' ? 'outcome-expired' : undefined}>{e.outcome}</span>,
        getSortValue: e => e.outcome,
    },
];

const episodeKey = (episode: FallbackEpisode) => `${episode.vehicleId}-${episode.tripId}-${episode.start}`;

const FallbackEpisodesTable: React.FC<Props> = ({ episodes, selectedEpisode, onSelect }) => {
    const isRowActive = useCallback((episode: FallbackEpisode) => episode === selectedEpisode, [selectedEpisode]);

    return (
        <SemanticDataTable
            className="fallback-episodes-table"
            columns={COLUMNS}
            data={episodes}
            rowKey={episodeKey}
            pageSize={PAGE_SIZE}
            compact
            singleLine
            onRowClick={onSelect}
            isRowActive={isRowActive}
        />
    );
};

export default React.memo(FallbackEpisodesTable);
